/* eslint-disable @typescript-eslint/naming-convention */

import { QuestionIdentifier } from "./question";
import { ImageQuality } from "./image-quality";

export interface ThumbnailImages {
    /** Normal thumbnail image */
    thumb_n: string;

    /** Hover image */
    thumb_h: string;
}

/** Key-value pair of ImageQuality and URLs */
export type ImageVariants = Record<string | ImageQuality.LOW | ImageQuality.MEDIUM | ImageQuality.HIGH, string>;

/** Smallest common base information to navigate to slide. */
export interface NavigationSlideInformation {
    id: number;
    // TODO: parentId is nullable in the REST API: get rid of 0 value in code. Use null value instead.
    parentId: number;
    type: SlideType;
    nodeType: NodeType;
}

/** Smallest common base information for any slide. */
export interface MinimumSlideInformation extends NavigationSlideInformation {
    caption: string;
    bordercolor: BorderColor;
    /** The URLs are always present, but the image is not always present in blob storage (e.g. text slide).  */
    previewpicture: ThumbnailImages;
    /** Indicate whether to show slide headline(i.e. caption) over slide image.*/
    showCaption: boolean;
}

export interface Slide extends MinimumSlideInformation, SlideBadge {
    moduleId: number;
    order: number;
    nextchapter: null | number;
    versionId: number;
    votingElementId?: number;
    hasIntegratedHotspot: boolean;
    driverscamCategories: number[];
    isHiddenStvoElement?: boolean;
    /** The slide should be hidden if true (when isOptimized===true),
     * it should be shown if false (when isOptimized===false),
     * the property should be ignored if undefined */
    isHiddenWhenUsingOptimizedStructure?: boolean;
    isArchived?: boolean;
    questions?: QuestionIdentifier[];
    isCustomerSlide: boolean;
    hasGuidance: boolean;

    /**
     * True if this lection is an e-learning lection.
     * Only set on lection nodes (nodeType === lection). When true, the lection's
     * preview thumbnail is displayed greyed out in the teacher app.
     */
    isELearningLection?: boolean;

    /**
     * (optional) array of question identifiers/numbers.
     * _Not_ fetched from the API but filled dynamically at runtime.
     */
    difficultQuestionNumbers?: QuestionIdentifier[];
}

export interface SlideBadge {
    badgeNew: boolean;
    badgeVideoQuestion: boolean;
    badgeStVO: boolean;
    badgeFeV: boolean;
}

export interface SlideDetails extends Slide, ImageInfo {
    usageResource: null | string;
    methodResource: null | string;
    noteResource: null | string;
    /** teachertexts added from user in a default module. */
    customerNote: null | string;

    /** teachertexts added from user in a customer module.
     * This is in normal cases null for default slides */
    customerNoteResource: null | string;

    imageVariants: ImageVariants;
    vimeoVideoId: null | number;
    videoRange?: null | VideoRange;
    videoPausePoints?: null | VideoPausePoint[];
    hotspots?: null | Hotspot[];
    interactiveElementUrl?: null | string;
    rubberElementPictures?: RubberElementPictures;
    bookpages?: Bookpage[];
    hideFavoriteButton?: boolean;
    hidePageButton?: boolean;
    scrollVideo?: ScrollVideo;
    lightTextColor?: boolean;
    qualityCriteria?: null | number[];

    /** Copyright / attribution credit for the slide image. */
    copyright?: null | string;

    examModuleTopicId?: number;
    examModuleTopicSheetId?: number;
}

export interface DriverscamSlideDetails extends SlideDetails {
    usageResource: null;
    methodResource: null;
    noteResource: null;
    customerNote: null;
    vimeoVideoId: number;
    hotspots?: null;
    interactiveElementUrl?: undefined;
    type: SlideType.driverscam;
    nextchapter: null;
    panorama?: undefined;
}

// DriverscamSlideDetails type guard
export const isDriverscamSlideDetails = (slide: SlideDetails | DriverscamSlideDetails): slide is DriverscamSlideDetails =>
    slide.type === SlideType.driverscam;

export enum SlideType {
    module = 'MO',          // Module (generated client side)
    lesson = 'L',           // Lektion / lesson
    chapter = 'K',          // Kaptiel / chapter
    text = 'T',             // simple text extension element
    graphic = 'G',          // graphic text extension element
    textboard = 'X',        // textboard basic slide (Grundlagenelement) for "simple text" elements
    graphicboard = 'XG',    // graphicboard basic slide (Grundlagenelement) for "graphic" elements
    video = 'V',
    picture = 'B',
    perceptionImage = 'W',
    interactive = 'I',
    rubber = 'R',
    voting = 'A',
    blendedLearning = 'D',  // "Selbstlerneinheiten", maps to setting "self learning units"
    driverscam = 'DCE',     // driverscam element
    driverscamChapter = "DC",
    difficultQuestion = "M",
    maxChapter = "MC",
    whiteboard = 'WB',
    textboard_generated = 'TB',     //textboard generated by teacher (via textboard assistant)
    bookpage = 'BP',
    gefahrenlernen = 'J',
    scrollVideo = 'O',
    executable = 'Q',
    vdo = 'VDO',            // VDO driving simulator (EMM), embedded in an iframe
    empty = '',              // reserved place in UI for scrolling
    placeholder = 'PLACEHOLDER', // Placeholder Slide in EditWorkspace to drag n drop file onto (or futher creation a new slide in place of it)
    goToParent = "GOTOPARENT",
    moodCheck = 'E'
}

export enum NodeType {
    unknown = "U",
    module = "M",
    lection = "L",
    chapter = "C",
    basic = "B",
    extension = "E",
    hidden = "H",
}

export enum BorderColor {
    transparent = 'transparent',  //used for helper-slides only (e.g. empty / goToParent)
    yellow = 'Y',
    gray = 'G',
    white = 'W',
    pink = 'X',
    orange = 'O',
    purple = 'P',
    blue = 'B',
    green = 'N',
    unknown = 'Z',
    black = 'black',
}

export const BorderColorMap = new Map<BorderColor, string>([
    [BorderColor.transparent, 'transparent'],
    [BorderColor.yellow, '#FCF000'],
    [BorderColor.gray, '#A0A0A0'],
    [BorderColor.white, '#FFFFFF'],
    [BorderColor.pink, '#B80651'],
    [BorderColor.orange, '#FF8001'],
    [BorderColor.purple, '#740CBA'],
    [BorderColor.blue, '#005AC3'],
    [BorderColor.green, '#007E3A'],
    [BorderColor.unknown, '#000000'],
    [BorderColor.black, 'black'],
]);

export enum BorderColorValue {
    'transparent',  //used for helper-slides only (e.g. empty / goToParent)
    '#FCF000', // yellow
    '#A0A0A0', // gray
    '#FFFFFF', // white
    '#B80651', // pink
    '#FF8001', // orange
    '#740CBA', // purple
    '#005AC3', // blue
    '#007E3A', // green
    '#000000', // unknown
    'black',
}

export interface Panorama {
    initialViewport: number | null;
    width: number;
    height: number;
    direction: 'Horizontal' | 'Vertical';
}

export interface Hotspot {
    width: number,
    height: number,
    x: number,
    y: number,
    type: HotspotType,
    targetSlideId: number
    targetModuleId: number
}

export interface VideoHotspot extends Hotspot {
    frameStart?: number;
    frameEnd?: number;
}

export const isVideoHotspot = (hotspot: Hotspot): hotspot is VideoHotspot =>
    [HotspotType.forward, HotspotType.element].includes(hotspot.type)
    && 'frameStart' in hotspot
    && 'frameEnd' in hotspot;

export enum HotspotType {
    integrated = 'I',
    forward = 'F',
    nested = 'N',
    /**
     * automatischer Hotspot.
     * Bei erreichen von FrameStart wird automatisch auf das Element in ActionLeft gesprungen.
     */
    element = 'E',
    /**
     * Hotspots of type 'O' are referncing a new slide.
     * The target slide will olways be shown above the current slide (new layer)
     * without changing the current navigation.
     *
     * The target slide can contain a 'back' button to return to the current slide.
     * When activated, the new layer will be closed and the "old" slide will be shown again,
     * keeping all dynamic content (drawings etc.).
     * The overlay slide will be closed if:
     * - the user clicks on any hotspot of type 'F' AND
     * - the clicked hotspot's targetSlideId === the current selectedSlideId (ID of the slide below).
     */
    overlay = 'O',

    moodcheck = 'C'
}

export interface RubberElementPictures {
    foregroundImageUrl: string;
    backgroundImageUrl: string;
}

export interface Bookpage extends ImageInfo {
    book: string;
    page: number;
    thumb: string;
}

export interface ImageInfo {
    type: SlideType;
    panorama?: null | Panorama;
    imageResource: string;
}

export interface ScrollVideo {
    template: string;
    start: number;
    end: number;
    current?: number;
    step: number;
}

export interface VideoRange {
    start: number;
    end: number;
}

export type VideoPausePoint = number;

export const isSlide = (slide: Slide | any): slide is Slide =>
    'id' in slide
    && 'moduleId' in slide
    && 'versionId' in slide
    && 'order' in slide;

export const isGoToParentSlide = (slide: Slide): boolean =>
    slide.type === SlideType.goToParent;

export const hasThumbPicture = (slide: MinimumSlideInformation): boolean => !!(slide.previewpicture?.thumb_n) && !!(slide.previewpicture?.thumb_h);
